import React, { useState } from 'react';
import { Check, Plus, AlertCircle, Circle, Star, Crown, Sparkles, RotateCcw } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * LevelTimeline - Visual level selector for unified character builder
 *
 * Shows all character levels (1 to current + next level option)
 * Click any level to edit that level's choices
 *
 * Visual states:
 * - Complete: green checkmark (all required choices made)
 * - Current: highlighted (highest level)
 * - Incomplete: red dot (missing required choices)
 * - Next: [+] button (advance to next level)
 *
 * @param {number} currentLevel - Character's current level (1-10)
 * @param {number} selectedLevel - Currently selected level in editor
 * @param {Function} onSelect - Callback when level is clicked
 * @param {Object} completionStatus - Object mapping level -> boolean (is complete)
 */
function LevelTimeline({
  currentLevel = 1,
  selectedLevel = 1,
  onSelect,
  completionStatus = {},
  character,
  onRevert
}) {
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  // Generate array of all levels [1, 2, 3, ..., current]
  const levels = Array.from({ length: currentLevel }, (_, i) => i + 1);

  // Check if can advance to next level (current level must be complete)
  const canAdvance = currentLevel < 10 && completionStatus[currentLevel] !== false;

  // Get tier icon for a level
  const getTierIcon = (level) => {
    if (level === 1) return Circle;    // Tier 1: Level 1
    if (level <= 4) return Star;       // Tier 2: Levels 2-4
    if (level <= 7) return Sparkles;   // Tier 3: Levels 5-7
    return Crown;                      // Tier 4: Levels 8-10
  };

  return (
    <>
      {/* Desktop: Vertical sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-sidebar lg:bg-surface-1 lg:fixed lg:left-0 lg:top-[73px] lg:bottom-0 lg:z-40 lg:shadow-2xl">
        {/* Character Identity Card */}
        <div className="px-4 py-4 space-y-2">
          {/* Character Name */}
          <h2 className="text-heading-lg text-red-400 font-bold truncate font-serif">
            {character?.name || 'New Character'}
          </h2>

          {/* Character Details */}
          <div className="text-sm space-y-1 text-gray-300">
            {/* Level, Class, Subclass */}
            <div className="font-medium">
              <span className="font-bold text-red-400">Level {character?.level || 1}</span>
              {' '}
              <span className="text-gray-100">{character?.class ? character.class.charAt(0).toUpperCase() + character.class.slice(1) : 'Class'}</span>
              {character?.subclass && (
                <>
                  <span className="text-gray-100">: </span>
                  <span className="text-gray-400">
                    {character.subclass
                      .split('-')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')
                    }
                  </span>
                </>
              )}
            </div>

            {/* Ancestry & Community */}
            {(character?.ancestry || character?.community) && (
              <div className="text-gray-400 text-xs">
                {typeof character?.ancestry === 'string'
                  ? character.ancestry.replace(/([A-Z])/g, ' $1').trim()
                  : Array.isArray(character?.ancestry)
                    ? character.ancestry.map(a => a.charAt(0).toUpperCase() + a.slice(1)).join('/')
                    : ''}
                {character?.community && (
                  <>
                    {character?.ancestry && ' - '}
                    {character.community.charAt(0).toUpperCase() + character.community.slice(1)}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 space-y-2 overflow-y-auto flex-1">
          <div className="mb-4">
            <h3 className="text-heading-sm text-red-400 font-bold font-serif">Character Levels</h3>
            <p className="text-tiny text-gray-400 mt-1">Build your hero's journey</p>
          </div>

          {levels.map(level => (
            <React.Fragment key={level}>
              {/* Tier 2 separator (before level 2) */}
              {level === 2 && (
                <div className="py-3 my-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent opacity-50" />
                    <span className="text-tiny text-gray-500 font-medium px-2">Tier 2</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent opacity-50" />
                  </div>
                </div>
              )}

              {/* Tier 3 separator (before level 5) */}
              {level === 5 && (
                <div className="py-3 my-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent opacity-50" />
                    <span className="text-tiny text-gray-500 font-medium px-2">Tier 3</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent opacity-50" />
                  </div>
                </div>
              )}

              {/* Tier 4 separator (before level 8) */}
              {level === 8 && (
                <div className="py-3 my-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent opacity-50" />
                    <span className="text-tiny text-gray-500 font-medium px-2">Tier 4</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent opacity-50" />
                  </div>
                </div>
              )}

              <LevelButton
                level={level}
                isSelected={selectedLevel === level}
                isCurrent={level === currentLevel}
                isComplete={completionStatus[level] !== false}
                onClick={() => onSelect(level)}
                TierIcon={getTierIcon(level)}
              />
            </React.Fragment>
          ))}

          {/* Next level button - styled like other level buttons */}
          {canAdvance && (
            <button
              onClick={() => onSelect(currentLevel + 1)}
              className={cn(
                "w-full px-4 py-4 rounded-lg transition-all duration-200 mb-1 mt-4",
                "flex items-center justify-between gap-4",
                "text-left relative",
                "focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset",
                selectedLevel === currentLevel + 1
                  ? "text-red-400 bg-red-900/20 border border-red-700/50"
                  : "text-gray-400 hover:text-red-300 hover:bg-red-900/10 border border-dashed border-border-default"
              )}
            >
              {/* Active indicator (left border) */}
              {selectedLevel === currentLevel + 1 && (
                <div
                  className="absolute left-0 top-1 bottom-1 w-1 bg-red-700 rounded-r-full shadow-lg shadow-red-700/50"
                  aria-hidden="true"
                />
              )}

              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Plus className={cn(
                  "w-6 h-6 transition-all flex-shrink-0",
                  selectedLevel === currentLevel + 1 ? "text-red-400" : "text-gray-400"
                )} />
                <div className="flex-1 min-w-0">
                  <span className={cn(
                    "text-heading-sm font-bold",
                    selectedLevel === currentLevel + 1 && "text-red-400"
                  )}>
                    Level {currentLevel + 1}
                  </span>
                </div>
              </div>

              {/* New badge */}
              <div className="flex-shrink-0">
                <div className="px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500 text-tiny text-green-400 font-medium">
                  NEW
                </div>
              </div>
            </button>
          )}

          {/* Revert button - only show if viewing a level below current max */}
          {selectedLevel > 1 && selectedLevel < currentLevel && onRevert && (
            <div className="mt-6 pt-6 border-t border-border-default">
              {showRevertConfirm ? (
                <div className="space-y-3">
                  <p className="text-tiny text-yellow-400 font-medium">
                    ⚠️ Revert to Level {selectedLevel}?
                  </p>
                  <p className="text-tiny text-gray-400">
                    This will delete all progress from levels {selectedLevel + 1}-{currentLevel} and cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        onRevert(selectedLevel);
                        setShowRevertConfirm(false);
                      }}
                      className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-tiny font-medium"
                    >
                      Confirm Revert
                    </button>
                    <button
                      onClick={() => setShowRevertConfirm(false)}
                      className="flex-1 px-3 py-2 bg-surface-3 text-gray-300 rounded-md hover:bg-surface-2 transition-colors text-tiny font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowRevertConfirm(true)}
                  className="w-full px-4 py-3 rounded-lg transition-all
                             flex items-center justify-center gap-2
                             bg-yellow-900/20 border border-yellow-700/50 text-yellow-400
                             hover:bg-yellow-900/30 hover:border-yellow-600"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="font-medium text-sm">Revert to Level {selectedLevel}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile: Fixed bottom navigation (matching character sheet pattern) */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900 to-transparent
                   border-t-2 border-red-700 shadow-2xl z-40 pb-safe"
        style={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}
      >
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-h-[56px] min-w-max px-2 items-center" style={{ scrollSnapType: 'x mandatory' }}>
            {levels.map(level => (
              <MobileLevelButton
                key={level}
                level={level}
                isSelected={selectedLevel === level}
                isCurrent={level === currentLevel}
                isComplete={completionStatus[level] !== false}
                onClick={() => onSelect(level)}
                TierIcon={getTierIcon(level)}
              />
            ))}

            {/* Next level button - matches tab style */}
            {canAdvance && (
              <button
                onClick={() => onSelect(currentLevel + 1)}
                className={cn(
                  // Base styles - fixed width instead of flex-1
                  "flex flex-col items-center justify-center gap-1",
                  "min-w-[80px] min-h-touch-min px-3 py-2",
                  "transition-all duration-200",
                  "relative",
                  // Active state - color only, no background/border
                  selectedLevel === currentLevel + 1
                    ? "text-green-400"
                    : "text-gray-400 hover:text-green-300"
                )}
                style={{ scrollSnapAlign: 'start' }}
              >
                <Plus
                  className={cn(
                    "w-6 h-6 transition-transform duration-200",
                    selectedLevel === currentLevel + 1 && "scale-110"
                  )}
                />
                <span className="text-tiny font-medium">Level {currentLevel + 1}</span>

                {/* Active indicator (bottom bar) */}
                {selectedLevel === currentLevel + 1 && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 bg-green-500 rounded-t-full shadow-lg shadow-green-500/50"
                    aria-hidden="true"
                  />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Scroll indicators (subtle fade on edges) */}
        <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-900 to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-900 to-transparent pointer-events-none" />
      </nav>
    </>
  );
}

/**
 * Desktop level button
 */
function LevelButton({ level, isSelected, isCurrent, isComplete, onClick, TierIcon }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full px-4 py-4 rounded-lg transition-all duration-200 mb-1",
        "flex items-center justify-between gap-4",
        "text-left relative",
        "focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset",
        isSelected
          ? "text-red-400 bg-red-900/20 border border-red-700/50"
          : "text-gray-400 hover:text-red-300 hover:bg-red-900/10"
      )}
    >
      {/* Active indicator (left border) */}
      {isSelected && (
        <div
          className="absolute left-0 top-1 bottom-1 w-1 bg-red-700 rounded-r-full shadow-lg shadow-red-700/50"
          aria-hidden="true"
        />
      )}

      <div className="flex items-center gap-3 flex-1 min-w-0">
        <TierIcon
          className={cn(
            "w-6 h-6 transition-all flex-shrink-0",
            isSelected
              ? level <= 4
                ? "text-red-400 fill-red-400/20"  // Circle gets fill when active
                : "text-red-400"
              : "text-gray-400"
          )}
        />
        <div className="flex-1 min-w-0">
          <span className={cn(
            "text-heading-sm font-bold",
            isSelected && "text-red-400"
          )}>
            Level {level}
          </span>
        </div>
      </div>

      {/* Status indicator */}
      <div className="flex-shrink-0">
        {isComplete ? (
          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shadow-lg">
            <Check className="w-4 h-4 text-white" />
          </div>
        ) : (
          <div className="w-6 h-6 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center">
            <AlertCircle className="w-4 h-4 text-red-500" />
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * Mobile level button (matches character sheet tab style exactly)
 */
function MobileLevelButton({ level, isSelected, isCurrent, isComplete, onClick, TierIcon }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        // Base styles - fixed width instead of flex-1 to prevent wrapping
        "flex flex-col items-center justify-center gap-1",
        "min-w-[80px] min-h-touch-min px-3 py-2",
        "transition-all duration-200",
        "relative",
        // Active state - color only, no background/border
        isSelected ? "text-red-400" : "text-gray-400 hover:text-red-300"
      )}
      style={{ scrollSnapAlign: 'start' }}
    >
      <TierIcon
        className={cn(
          "w-6 h-6 transition-transform duration-200",
          isSelected && "scale-110"
        )}
      />
      <span className="text-tiny font-medium">Level {level}</span>

      {/* Active indicator (bottom bar) */}
      {isSelected && (
        <div
          className="absolute bottom-0 left-0 right-0 h-1 bg-red-700 rounded-t-full shadow-lg shadow-red-700/50"
          aria-hidden="true"
        />
      )}

      {/* Current badge - small dot indicator */}
      {isCurrent && !isSelected && (
        <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-700 border-2 border-gray-900 shadow-lg shadow-red-700/50" />
      )}
    </button>
  );
}

export default LevelTimeline;
